import React from "react";
import { Crown, Shield, Palette, Newspaper, BarChart3, Users, CheckCircle2, Coins, Mail, Sparkles, Star, Clock } from "lucide-react";

export const ROLES = [
    { Icon: Shield, name: "Roster Admin", color: "#9D4CDD", blurb: "Owns player records, lineups & rosters for assigned teams. Keeps every card accurate game-to-game.", tags: ["Players", "Lineups", "Rosters"] },
    { Icon: BarChart3, name: "Stats & Standings", color: "#38BDF8", blurb: "Syncs box scores, verifies leaderboards and keeps the standings table clean all season long.", tags: ["Game Centre", "Standings", "Leaderboards"] },
    { Icon: Palette, name: "Story Creator", color: "#F472B6", blurb: "Designs & publishes the daily story templates — recaps, three-stars, milestones and the social feed.", tags: ["Studio", "Templates", "Social"] },
    { Icon: Newspaper, name: "News & Media", color: "#34D399", blurb: "Writes headlines, curates videos and runs the newsroom so the league feels alive every day.", tags: ["News", "Videos", "Headlines"] },
];

export const TIERS = [
    { name: "Contributor", price: "Free", color: "#9CA3AF", perks: ["Single-team scope", "Edit players & lineups", "Community credit"], featured: false },
    { name: "Editor", price: "$25 / season", color: "#38BDF8", perks: ["Multi-team scope", "Stats + standings tools", "Story Creator access", "Name in the credits"], featured: true },
    { name: "Partner", price: "$100 / season", color: "#F6C453", perks: ["League-wide access", "Own a full section", "Priority feature requests", "Revenue share on packs"], featured: false },
];

export const RESPONSIBILITIES = [
    "Keep your assigned data accurate and up to date after every game night",
    "Follow the Rosterly style guide for names, teams, and story templates",
    "Never delete another admin's work — everything is now logged",
    "Flag anything unusual to the owner before making sweeping changes",
];

function SectionTitle({ Icon, children }) {
    return (
        <h3 className="flex items-center gap-2 font-heading text-lg font-black uppercase tracking-tight text-white mb-3">
            <Icon className="h-4 w-4 text-[#F6C453]" /> {children}
        </h3>
    );
}

export default function AdminHub() {
    return (
        <div className="space-y-8" data-testid="admin-hub">
            {/* Pitch */}
            <div className="rounded-2xl border border-[#F6C453]/20 bg-gradient-to-br from-[#1a1405] to-[#0A0B10] p-6 sm:p-8">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FCD34D] flex items-center gap-1.5 mb-2"><Sparkles className="h-3 w-3" /> Join the crew</p>
                <h2 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-white max-w-2xl">Run a corner of the fastest-growing women's hockey hub</h2>
                <p className="text-sm text-white/70 max-w-2xl pt-2 leading-relaxed">
                    Rosterly is built by fans, for fans. We're bringing on a small crew of trusted admins to own the data, the stories and the day-to-day polish. Pick a lane below, learn the buy-in, and drop us a line.
                </p>
                <a href="mailto:max@teamrosterly.com?subject=Rosterly%20Admin%20Application" data-testid="admin-hub-apply" className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-[#F6C453] text-black text-xs font-black uppercase tracking-wider hover:bg-[#f4b830] transition-colors">
                    <Mail className="h-4 w-4" /> Apply to join
                </a>
            </div>

            {/* Open roles */}
            <section>
                <SectionTitle Icon={Users}>Open roles</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {ROLES.map((r) => (
                        <div key={r.name} className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 hover:border-white/20 transition-colors" data-testid={`hub-role-${r.name}`}>
                            <div className="flex items-center gap-3 mb-2">
                                <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${r.color}1f`, border: `1px solid ${r.color}59` }}>
                                    <r.Icon className="h-5 w-5" style={{ color: r.color }} />
                                </span>
                                <h4 className="font-heading text-lg font-bold uppercase tracking-tight text-white">{r.name}</h4>
                            </div>
                            <p className="text-sm text-white/65 leading-relaxed">{r.blurb}</p>
                            <div className="flex flex-wrap gap-1.5 mt-3">
                                {r.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white/55 bg-white/[0.05] border border-white/10">{t}</span>)}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Buy-in tiers */}
            <section>
                <SectionTitle Icon={Coins}>Buy-in & access tiers</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {TIERS.map((t) => (
                        <div key={t.name} className={`relative rounded-2xl p-5 border ${t.featured ? "border-[#F6C453]/40 bg-[#F6C453]/[0.05]" : "border-white/10 bg-white/[0.03]"}`} data-testid={`hub-tier-${t.name}`}>
                            {t.featured && <span className="absolute -top-2.5 left-5 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-[#F6C453] text-black flex items-center gap-1"><Star className="h-2.5 w-2.5" /> Most popular</span>}
                            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: t.color }}>{t.name}</p>
                            <p className="font-heading text-2xl font-black text-white mt-1">{t.price}</p>
                            <ul className="mt-4 space-y-2">
                                {t.perks.map((p) => (
                                    <li key={p} className="flex items-start gap-2 text-sm text-white/70">
                                        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: t.color }} /> {p}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
                <p className="text-[11px] text-white/40 mt-3">Buy-in funds hosting, data feeds and pack rewards. It's a contribution, not a salary — everyone here does it for the love of the game.</p>
            </section>

            {/* Responsibilities */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                    <SectionTitle Icon={CheckCircle2}>What we expect</SectionTitle>
                    <ul className="space-y-2.5">
                        {RESPONSIBILITIES.map((r) => (
                            <li key={r} className="flex items-start gap-2 text-sm text-white/70"><span className="h-1.5 w-1.5 rounded-full bg-[#F6C453] mt-1.5 shrink-0" /> {r}</li>
                        ))}
                    </ul>
                </div>
                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                    <SectionTitle Icon={Clock}>How it works</SectionTitle>
                    <ol className="space-y-3">
                        {["Apply with the role you want + a bit about you", "Quick chat with the owner to align on scope", "Pick your tier & get your scoped admin login", "Start editing — every change is auto-logged"].map((s, i) => (
                            <li key={s} className="flex items-start gap-3 text-sm text-white/70">
                                <span className="h-6 w-6 rounded-full bg-[#F6C453]/15 text-[#F6C453] text-xs font-black flex items-center justify-center shrink-0">{i + 1}</span> {s}
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-[#0E0F16] to-[#1a1405] p-6 text-center">
                <Crown className="h-6 w-6 text-[#F6C453] mx-auto mb-2" />
                <p className="text-sm text-white/70 max-w-xl mx-auto">Ready to help build the definitive home for women's hockey? We'd love to have you.</p>
                <a href="mailto:max@teamrosterly.com?subject=Rosterly%20Admin%20Application" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-wider transition-colors">
                    <Mail className="h-4 w-4" /> max@teamrosterly.com
                </a>
            </div>
        </div>
    );
}
